import { Controller } from "@hotwired/stimulus";

// Stimulus controller to initialize Shaka Player for a <video> element
// Expects data-hls-url (or data-dash-url) and optional data-mp4-url on the element
// If data-lazy-shaka="true", initialization is deferred until explicitly triggered
export default class extends Controller {
  player = null;

  async connect() {
    const videoEl = this.element;
    if (!(videoEl instanceof HTMLVideoElement)) return;

    // Skip initialization if lazy loading is enabled
    // The gallery controller will trigger initialization when visible
    if (videoEl.dataset.lazyShaka === 'true') {
      // Listen for explicit init trigger from gallery
      this.boundInitHandler = this.initPlayer.bind(this);
      videoEl.addEventListener('video:init', this.boundInitHandler);
      return;
    }

    await this.initPlayer();
  }

  async initPlayer() {
    const videoEl = this.element;
    if (!(videoEl instanceof HTMLVideoElement)) return;

    // Prevent double-initialization
    if (videoEl.dataset.shakaInitialized === 'true') return;
    videoEl.dataset.shakaInitialized = 'true';

    const manifestUrl = videoEl.dataset.hlsUrl || videoEl.dataset.dashUrl;
    const mp4Url = videoEl.dataset.mp4Url;
    const poster = videoEl.getAttribute('poster');
    if (!manifestUrl) return;

    try {
      // Lazy import to avoid loading Shaka unless needed
      const module = await import(/* webpackChunkName: "shaka_player" */ '../../../client/js/common/shaka_player');
      const { initShakaOn } = module;
      this.player = await initShakaOn(videoEl, manifestUrl, mp4Url, { autoplay: videoEl.autoplay, poster });
    } catch (err) {
      console.error('[Stimulus][video-player] Failed to init Shaka:', err && err.message);
      // As a last resort, if MP4 is present and not yet set, use it
      if (mp4Url && !videoEl.getAttribute('src')) {
        videoEl.setAttribute('src', mp4Url);
        videoEl.setAttribute('type', 'video/mp4');
      }
    }
  }

  async disconnect() {
    // Clean up event listener
    if (this.boundInitHandler && this.element) {
      this.element.removeEventListener('video:init', this.boundInitHandler);
    }

    try {
      if (this.player && typeof this.player.destroy === 'function') {
        await this.player.destroy();
      }
    } catch (_) {
      // ignore
    } finally {
      this.player = null;
    }
  }
}


